import React, { useMemo, useState } from 'react';
import { Box, Chip, Dialog, DialogContent, Typography, useTheme } from '@mui/material';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSubmission } from 'services/Data/SubmissionServices';
import { translateLabel } from 'services/Utilities/labelUtils';
import { getSubmissionDetailStatusConfig, SubmissionDetailStatusEnumValue } from 'enums/SubmissionDetailStatusEnum';
import RenderItem from './RenderItem';

// Checklist item supports nested children; uses submission cache for status chips
const ChecklistItem = ({ detail, allDetails = [], level = 0, isReadOnly = false }) => {
  const theme = useTheme();
  const { i18n } = useTranslation();
  const { submissionId } = useParams();
  const { data: submission } = useSubmission(submissionId);
  const [viewerImage, setViewerImage] = useState('');

  const indentAmount = level * 4;

  const translatedDescription = useMemo(() => translateLabel(detail.description, i18n.language), [
    detail.description,
    i18n.language,
  ]);

  const descriptionSegments = useMemo(() => {
    if (!translatedDescription) {
      return [];
    }

    return translatedDescription
      .split(/\r?\n/)
      .map((segment, index) => {
        const trimmed = segment.trim();

        if (!trimmed) {
          return null;
        }

        if (trimmed.startsWith('data:image/') && trimmed.includes(';base64,')) {
          return { key: `img-${detail.id}-${index}`, type: 'image', src: trimmed };
        }

        return { key: `text-${detail.id}-${index}`, type: 'text', text: segment };
      })
      .filter(Boolean);
  }, [detail.id, translatedDescription]);

  const hasImageSegments = descriptionSegments.some(segment => segment.type === 'image');

  const existingSubmissionDetail = submission?.submissionDetails?.find(sd => sd.criteriaTypeDetail_Id === detail.id);
  const currentStatus = existingSubmissionDetail?.status ?? SubmissionDetailStatusEnumValue.Pending;
  const isPending = currentStatus === SubmissionDetailStatusEnumValue.Pending;
  const isApproved = currentStatus === SubmissionDetailStatusEnumValue.Accepted;
  const showStatusChip =
    currentStatus === SubmissionDetailStatusEnumValue.NeedsWork ||
    currentStatus === SubmissionDetailStatusEnumValue.ImprovementRequired ||
    currentStatus === SubmissionDetailStatusEnumValue.BrpBestPractice;
  const statusConfig = showStatusChip ? getSubmissionDetailStatusConfig(currentStatus) : null;
  const statusAccent = statusConfig?.color || '#EF5350';
  const statusBg =
    statusConfig?.bgColor || (theme.palette.mode === 'dark' ? 'rgba(239, 83, 80, 0.18)' : 'rgba(239, 83, 80, 0.12)');
  const statusHoverBg =
    statusConfig?.bgColor || (theme.palette.mode === 'dark' ? 'rgba(239, 83, 80, 0.26)' : 'rgba(239, 83, 80, 0.18)');

  const childDetails = allDetails
    .filter(child => child.parentCriteriaTypeDetail_Id === detail.id)
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'top',
          gap: 1,
          pl: indentAmount,
          py: 0.5,
          transition: 'background-color 0.2s ease',
          bgcolor: showStatusChip ? statusBg : 'transparent',
          borderLeft: `3px solid ${showStatusChip ? statusAccent : 'transparent'}`,
          borderRadius: 1,
          '&:hover': {
            bgcolor: showStatusChip
              ? statusHoverBg
              : theme.palette.mode === 'dark'
              ? 'rgba(255, 210, 0, 0.05)'
              : 'rgba(212, 175, 55, 0.05)',
          },
        }}>
        {isPending || isApproved ? (
          <Box
            component="span"
            sx={{
              width: '6px',
              height: '6px',
              borderRadius: '50%',
              mt: '10px',
              bgcolor: theme.palette.mode === 'dark' ? 'rgba(212, 175, 55, 0.6)' : 'rgba(212, 175, 55, 0.8)',
              flexShrink: 0,
            }}
          />
        ) : statusConfig?.icon ? (
          <Box component={statusConfig.icon} sx={{ fontSize: 16, mt: '6px', color: statusAccent, flexShrink: 0 }} />
        ) : (
          <Box
            component="span"
            sx={{
              width: '6px',
              height: '6px',
              borderRadius: '50%',
              mt: '10px',
              bgcolor: theme.palette.mode === 'dark' ? 'rgba(212, 175, 55, 0.6)' : 'rgba(212, 175, 55, 0.8)',
              flexShrink: 0,
            }}
          />
        )}
        <Typography
          variant="body2"
          sx={{
            fontWeight: level === 0 ? 600 : 400,
            color: showStatusChip
              ? statusAccent
              : theme.palette.mode === 'dark'
              ? 'rgba(255, 255, 255, 0.7)'
              : 'rgba(0, 0, 0, 0.6)',
            transition: 'color 0.2s ease',
          }}>
          {translateLabel(detail.name, i18n.language)}
        </Typography>
        {showStatusChip && statusConfig && (
          <Chip
            label={i18n.language === 'fr' ? statusConfig.labelFrench : statusConfig.labelEnglish}
            size="small"
            sx={{
              ml: 'auto',
              fontWeight: 700,
              bgcolor: statusConfig.bgColor || statusBg,
              color: statusAccent,
              border: `1px solid ${statusAccent}`,
              textTransform: 'uppercase',
              letterSpacing: '0.06em',
            }}
          />
        )}
      </Box>

      {descriptionSegments.length > 0 && (
        <Box sx={{ pl: indentAmount + 2, pr: 2, pb: 1, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {descriptionSegments.map(segment =>
            segment.type === 'image' ? (
              <Box
                key={segment.key}
                component="img"
                src={segment.src}
                alt={translateLabel(detail.name, i18n.language)}
                role="button"
                tabIndex={0}
                onClick={() => setViewerImage(segment.src)}
                onKeyDown={event => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    setViewerImage(segment.src);
                  }
                }}
                sx={{ width: '50%', borderRadius: 1, objectFit: 'contain', maxHeight: 320, cursor: 'zoom-in' }}
              />
            ) : (
              <Typography
                key={segment.key}
                variant="caption"
                sx={{
                  color: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.55)' : 'rgba(0, 0, 0, 0.55)',
                  whiteSpace: 'pre-line',
                }}>
                {segment.text}
              </Typography>
            ),
          )}
        </Box>
      )}

      {hasImageSegments && (
        <Dialog open={Boolean(viewerImage)} onClose={() => setViewerImage('')} maxWidth="md" fullWidth>
          <DialogContent sx={{ p: 0, bgcolor: 'rgba(0,0,0,0.85)' }}>
            {viewerImage && (
              <Box
                component="img"
                src={viewerImage}
                alt={translateLabel(detail.name, i18n.language)}
                sx={{ width: '100%', display: 'block', objectFit: 'contain', maxHeight: '80vh' }}
              />
            )}
          </DialogContent>
        </Dialog>
      )}

      {childDetails.length > 0 && (
        <Box>
          {childDetails.map(childDetail => (
            <RenderItem key={childDetail.id} detail={childDetail} allDetails={allDetails} isReadOnly={isReadOnly} />
          ))}
        </Box>
      )}
    </Box>
  );
};

export default ChecklistItem;
